// The invitation e-mail. server/src/lib/email/invitationEmail.ts and
// client/src/components/Forms/SurveyWizard/invitationEmail.ts are the same file, character for character:
// the server sends it and the survey wizard previews it. server/__tests__/emailTemplates.test.ts checks it.
// Inline styles on tables and no <style> block, since many e-mail clients strip the block.

export type InvitationEmailInput = {
	lang: string;
	candidate: string;
	message: string;
	url: string;
};

type Link = (href: string, text: string) => string;

type Copy = {
	htmlLang: string;
	preheader: (name: string) => string;
	heading: (name: string) => string;
	body: (name: string) => string;
	button: string;
	fallback: string;
	readMore: (name: string, link: Link) => string;
	privacy: (name: string, link: Link) => string;
};

const ARTICLE_URL = 'https://feedbackjourney.com/feedback-journey-for-clients';
const PRIVACY_URL = 'https://feedbackjourney.com/privacy';

const copy: Record<'en' | 'no', Copy> = {
	en: {
		htmlLang: 'en',
		preheader: (name) => `${name} would like to hear what you think. It takes less than a minute.`,
		heading: (name) => `${name} has invited you to give feedback`,
		body: (name) =>
			`It takes less than a minute to answer, and it will be of great help for ${name}. Maybe it will also strengthen the deliveries and the relationship between you?`,
		button: 'Give feedback',
		fallback: 'Button not working? Open this link:',
		readMore: (name, link) =>
			`If you want to know more about Feedback Journey, you can read ${link(ARTICLE_URL, 'our article explaining how Feedback Journey is valuable')} for you as a client or colleague of ${name}.`,
		privacy: (name, link) =>
			`Your response is handled in line with our ${link(PRIVACY_URL, 'privacy policy')}. ${name} may optionally use an AI feature (Google Gemini) to help interpret the feedback.`,
	},
	no: {
		htmlLang: 'nb',
		preheader: (name) => `${name} vil gjerne høre hva du mener. Det tar under ett minutt.`,
		heading: (name) => `${name} har invitert deg til å gi tilbakemelding`,
		body: (name) =>
			`Det tar under ett minutt å svare, og det er til stor hjelp for ${name}. Kanskje det også styrker leveransen og relasjonen mellom dere?`,
		button: 'Gi tilbakemelding',
		fallback: 'Virker ikke knappen? Åpne denne lenken:',
		readMore: (name, link) =>
			`Hvis du vil vite mer om Feedback Journey, kan du lese ${link(ARTICLE_URL, 'vår artikkel som forklarer hvordan Feedback Journey er verdifullt')} for deg som en kunde eller kollega av ${name}.`,
		privacy: (name, link) =>
			`Svaret ditt behandles i tråd med vår ${link(PRIVACY_URL, 'personvernerklæring')}. ${name} kan velge å bruke en AI-funksjon (Google Gemini) for å tolke tilbakemeldingen.`,
	},
};

const copyFor = (lang: string) => (lang === 'no' ? copy.no : copy.en);

const SANS = "'Kantumruy Pro', Helvetica, Arial, sans-serif";
const SERIF = "Unna, Georgia, 'Times New Roman', serif";

// For text from the user that goes into the HTML of an e-mail. & first, so the entities are not escaped twice.
export const escapeHtml = (str: string) =>
	str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

export const nl2br = (str: string) => str.replace(/\n/g, '<br />');

// The cream page with the wordmark, the white card and the footer. The wizard shows this part as the preview.
export const renderInvitationContent = ({ lang, candidate, message, url }: InvitationEmailInput) => {
	const c = copyFor(lang);
	const name = escapeHtml(candidate);
	const href = escapeHtml(url);
	const link: Link = (to, text) => `<a href="${to}" style="color: #B5603F; text-decoration: underline;">${text}</a>`;

	return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#F6F1E8" style="width: 100%; background-color: #F6F1E8;">
<tr><td align="center" style="padding: 32px 12px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; max-width: 560px;">
<tr><td style="padding: 0 4px 20px; font-family: ${SERIF}; font-size: 22px; line-height: 28px; color: #B5603F;"><strong>Feedback</strong> Journey</td></tr>
<tr><td bgcolor="#FFFFFF" style="background-color: #FFFFFF; border-radius: 12px; padding: 32px 24px; font-family: ${SANS}; font-size: 16px; line-height: 26px; color: #17202A;">
<h1 style="margin: 0 0 20px; font-family: ${SERIF}; font-size: 26px; line-height: 32px; font-weight: 400; color: #0F2B3C;">${c.heading(name)}</h1>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
<tr><td bgcolor="#F6F1E8" style="background-color: #F6F1E8; border-left: 4px solid #F08A6C; padding: 16px 20px; font-style: italic; color: #17202A;">${nl2br(escapeHtml(message))}</td></tr>
</table>
<p style="margin: 20px 0 0;">${c.body(name)}</p>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin: 28px 0 0;">
<tr><td align="center" bgcolor="#F08A6C" style="background-color: #F08A6C; border-radius: 999px;"><a href="${href}" style="display: inline-block; padding: 14px 32px; font-family: ${SANS}; font-size: 17px; line-height: 20px; font-weight: 400; color: #0F2B3C; text-decoration: none; border-radius: 999px;">${c.button}</a></td></tr>
</table>
<p style="margin: 20px 0 0; font-size: 14px; line-height: 22px; color: #5B6B73;">${c.fallback}<br /><a href="${href}" style="color: #B5603F; word-break: break-all;">${href}</a></p>
</td></tr>
<tr><td style="padding: 20px 4px 0; font-family: ${SANS}; font-size: 13px; line-height: 20px; color: #5B6B73;">
<p style="margin: 0 0 8px;">${c.readMore(name, link)}</p>
<p style="margin: 0;">${c.privacy(name, link)}</p>
</td></tr>
</table>
</td></tr>
</table>`;
};

// The whole HTML document that is sent, with a hidden preheader the inbox shows after the subject.
export const renderInvitationEmail = (input: InvitationEmailInput) => {
	const c = copyFor(input.lang);
	const name = escapeHtml(input.candidate);

	return `<!DOCTYPE html>
<html lang="${c.htmlLang}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<meta name="color-scheme" content="light" />
<meta name="supported-color-schemes" content="light" />
<title>${c.heading(name)}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #F6F1E8;">
<div style="display: none; max-height: 0; overflow: hidden; opacity: 0;">${c.preheader(name)}</div>
${renderInvitationContent(input)}
</body>
</html>`;
};

// The text part, for clients that show the e-mail as text. The address stands on its own line.
export const renderInvitationText = ({ lang, candidate, message, url }: InvitationEmailInput) => {
	const c = copyFor(lang);
	const link: Link = (to, text) => `${text} (${to})`;

	return `${[
		c.heading(candidate),
		message,
		c.body(candidate),
		`${c.button}:\n${url}`,
		c.readMore(candidate, link),
		c.privacy(candidate, link),
	].join('\n\n')}\n`;
};
